// Browser-style omnibox autocomplete. One shared dropdown is positioned under
// whichever input it's attached to. Suggestions come from three sources, in
// browser order: local history/favorites matches, the search/go-to action, then
// live web suggestions from the same-origin /suggest proxy (toggleable).
import { looksUrl } from "./navigation.js";
import * as history from "./history.js";
import * as favorites from "./favorites.js";
import { get } from "../shared/settings-store.js";

const ENGINE_NAME = { duckduckgo: "DuckDuckGo", google: "Google", bing: "Bing", custom: "Search" };

const SEARCH_ICON = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"><circle cx="7" cy="7" r="4.5"></circle><line x1="10.5" y1="10.5" x2="14" y2="14"></line></svg>';
const HIST_ICON = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"><circle cx="8" cy="8" r="6"></circle><polyline points="8,4.6 8,8 10.4,9.6"></polyline></svg>';
const GLOBE_ICON = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.2"><circle cx="8" cy="8" r="6"></circle><line x1="2" y1="8" x2="14" y2="8"></line><path d="M8 2 C5.5 4 5.5 12 8 14 C10.5 12 10.5 4 8 2 Z"></path></svg>';

let box = null;
let curInput = null;
let curCommit = null;
let items = [];
let sel = -1;
let query = "";
let debounce = null;
let aborter = null;

function ensureBox() {
	if (box) return box;
	box = document.createElement("div");
	box.className = "rv-suggest";
	box.setAttribute("data-no-obf", ""); // the obfuscation observer skips this subtree
	box.setAttribute("role", "listbox");
	box.id = "rv-suggest";
	box.setAttribute("aria-label", "Suggestions");
	box.hidden = true;
	box.addEventListener("mousedown", (e) => e.preventDefault()); // don't blur the input before a row click lands
	document.body.appendChild(box);
	window.addEventListener("resize", hide);
	window.addEventListener("scroll", (e) => { if (box && !box.hidden && !box.contains(e.target)) position(); }, true);
	return box;
}

// Combobox state on the input, so screen readers know a list is open and which row is chosen.
function syncInput() {
	if (!curInput) return;
	const open = !!(box && !box.hidden && items.length);
	curInput.setAttribute("aria-expanded", open ? "true" : "false");
	if (open && sel >= 0) curInput.setAttribute("aria-activedescendant", "rv-suggest-" + sel);
	else curInput.removeAttribute("aria-activedescendant");
}

function hide() {
	if (box) { box.hidden = true; box.textContent = ""; }
	items = []; sel = -1;
	syncInput();
	if (aborter) { aborter.abort(); aborter = null; }
}

function esc(s) { const d = document.createElement("div"); d.textContent = s == null ? "" : String(s); return d.innerHTML; }

// Bold the typed query where it appears inside a suggestion label.
function mark(text, q) {
	const i = q ? text.toLowerCase().indexOf(q.toLowerCase()) : -1;
	if (i < 0) return esc(text);
	return esc(text.slice(0, i)) + "<b>" + esc(text.slice(i, i + q.length)) + "</b>" + esc(text.slice(i + q.length));
}

function localMatches(q) {
	const lq = q.toLowerCase();
	const out = [];
	const seen = new Set();
	const push = (url, label) => {
		if (!url || seen.has(url)) return;
		seen.add(url);
		out.push({ value: url, label: label || url, sub: url, icon: HIST_ICON });
	};
	history.list().forEach((h) => { if (out.length < 3 && (h.url + " " + (h.title || "")).toLowerCase().indexOf(lq) !== -1) push(h.url, h.title); });
	favorites.list().forEach((f) => {
		if (out.length >= 3 || (f.type && f.type !== "site")) return;
		const t = f.title || f.name || f.url;
		if (((f.url || "") + " " + t).toLowerCase().indexOf(lq) !== -1) push(f.url, t);
	});
	return out;
}

function buildBase(q) {
	const list = localMatches(q);
	if (looksUrl(q)) {
		list.push({ value: q, label: q, sub: "Open site", icon: GLOBE_ICON });
	} else {
		const eng = get("searchEngine") || "duckduckgo";
		list.push({ value: q, label: q, sub: (ENGINE_NAME[eng] || "Web") + " Search", icon: SEARCH_ICON });
	}
	return list;
}

function position() {
	if (!curInput || !box) return;
	const r = curInput.getBoundingClientRect();
	box.style.left = r.left + "px";
	box.style.top = r.bottom + 5 + "px";
	box.style.width = r.width + "px";
}

function setSel(i) {
	sel = i;
	[...box.children].forEach((el, j) => { const on = j === sel; el.classList.toggle("sel", on); el.setAttribute("aria-selected", on ? "true" : "false"); });
	syncInput();
}

function render() {
	const b = ensureBox();
	b.textContent = "";
	items.forEach((it, i) => {
		const row = document.createElement("div");
		row.className = "rv-suggest-item" + (i === sel ? " sel" : "");
		row.setAttribute("role", "option");
		row.id = "rv-suggest-" + i;
		row.setAttribute("aria-selected", i === sel ? "true" : "false");
		row.innerHTML =
			'<span class="rv-suggest-ic">' + it.icon + "</span>" +
			'<span class="rv-suggest-label">' + mark(it.label, query) + "</span>" +
			(it.sub ? '<span class="rv-suggest-sub">' + esc(it.sub) + "</span>" : "");
		row.addEventListener("mousemove", () => setSel(i));
		row.addEventListener("click", () => commit(it));
		b.appendChild(row);
	});
	position();
	b.hidden = items.length === 0;
	syncInput();
}

function commit(it) {
	if (!it) return;
	const onCommit = curCommit;
	const input = curInput;
	if (input) input.value = it.value;
	hide();
	if (onCommit) onCommit(it.value);
	if (input) input.blur();
}

async function fetchRemote(q) {
	if (get("suggestions") === false) return;
	if (aborter) aborter.abort();
	aborter = new AbortController();
	const eng = get("searchEngine") || "duckduckgo";
	try {
		// POST so the typed text stays out of the URL (and out of access logs and the SW cache).
		const r = await fetch("/suggest", {
			method: "POST",
			headers: { "content-type": "application/json" },
			body: JSON.stringify({ q, engine: eng }),
			cache: "no-store",
			credentials: "omit",
			signal: aborter.signal,
		});
		if (!r.ok || q !== query) return;
		const list = await r.json();
		if (q !== query) return; // the user typed more while we waited
		const have = new Set(items.map((x) => x.value.toLowerCase()));
		(Array.isArray(list) ? list : []).forEach((s) => {
			if (typeof s !== "string" || have.has(s.toLowerCase())) return;
			have.add(s.toLowerCase());
			items.push({ value: s, label: s, icon: SEARCH_ICON });
		});
		items = items.slice(0, 10);
		render();
	} catch (e) {}
}

function onInput() {
	query = (curInput.value || "").trim();
	if (!query) { hide(); return; }
	items = buildBase(query);
	sel = -1;
	render();
	clearTimeout(debounce);
	const q = query;
	debounce = setTimeout(() => fetchRemote(q), 120);
}

function onKey(e) {
	if (!box || box.hidden || !items.length) return;
	if (e.key === "ArrowDown") { e.preventDefault(); setSel((sel + 1) % items.length); }
	else if (e.key === "ArrowUp") { e.preventDefault(); setSel((sel - 1 + items.length) % items.length); }
	else if (e.key === "Enter") { if (sel >= 0) { e.preventDefault(); commit(items[sel]); } else hide(); } // no selection → let the form submit run
	else if (e.key === "Escape") { e.preventDefault(); hide(); }
}

// Wire an input to the shared dropdown. onCommit(value) performs the navigation.
export function attachAutofill(input, onCommit) {
	if (!input) return;
	ensureBox();
	input.setAttribute("role", "combobox");
	input.setAttribute("aria-autocomplete", "list");
	input.setAttribute("aria-controls", "rv-suggest");
	input.setAttribute("aria-expanded", "false");
	const focus = () => { curInput = input; curCommit = onCommit; };
	input.addEventListener("input", () => { focus(); onInput(); });
	input.addEventListener("keydown", (e) => { focus(); onKey(e); });
	input.addEventListener("focus", focus);
	input.addEventListener("blur", () => setTimeout(hide, 130));
}
